import { dapBffApiV2 } from '@dap/bff/data-access';
import { RootState } from '@dap/common/data-access';
import {
  Interactions,
  SocialFeed,
  SocialFeedRequest,
  SocialFeedPost,
  SocialFeedPostsRequest,
  SocialFeedsRequest,
  AddPostResponse,
  AddPostRequest,
  EditPostPayload,
  GetInteractionsRequest,
  CombinedSocialFeed,
  CommonSocialFeedRequest,
  CommonSocialFeed,
  FeedPostsPayload,
  DeletePostPayload,
  ToggleNotificationPayload,
  ToggleNotificationResponse,
  GetNotificationStatusRequest,
  GetNotificationStatusResponse,
} from '@dap/sanity/types';
import { FullTagDescription } from '@reduxjs/toolkit/dist/query/endpointDefinitions';

const tags = [
  'SocialFeed',
  'SocialFeedPost',
  'SocialFeedPosts',
  'Interactions',
  'FeedPosts',
  'NotificationStatus',
  // Registered here (in addition to @dap/notifications/data-access) so toggling a
  // subscription from a social feed page can also refresh an open settings page.
  'NotificationSubscriptions',
];

const socialFeedApi = dapBffApiV2.enhanceEndpoints({ addTagTypes: tags }).injectEndpoints({
  endpoints: (build) => ({
    // Fetches a single social feed
    getSocialFeed: build.query<SocialFeed, SocialFeedRequest>({
      query: (props) => ({
        url: `/socialFeed/brand/${props.brandKey}/feed/${props.slug}`,
        method: 'GET',
      }),
      transformResponse: (response: { data: SocialFeed }) => response.data,
      providesTags: (result) => {
        if (!result) {
          return [];
        }
        return [{ type: 'SocialFeed', id: result._id }];
      },
    }),

    // Fetches a single common social feed
    getCommonSocialFeed: build.query<CommonSocialFeed, CommonSocialFeedRequest>({
      query: (props) => ({
        url: `/socialFeed/feed/common/${props.slug}`,
        method: 'GET',
      }),
      transformResponse: (response: { data: CommonSocialFeed }) => response.data,
      providesTags: (result) => {
        if (!result) {
          return [];
        }
        return [{ type: 'SocialFeed', id: result._id }];
      },
    }),

    // Fetches all social feeds for a brand, both common and brand specific
    getSocialFeeds: build.query<CombinedSocialFeed[], SocialFeedsRequest>({
      query: (props) => ({
        url: `/socialFeed/brand/${props.brandKey}/feeds`,
        method: 'GET',
      }),
      transformResponse: (response: { data: CombinedSocialFeed[] }) => response.data,
      providesTags: (result) => {
        if (!result) {
          return [];
        }
        return result.map((feed) => ({ type: 'SocialFeed', id: feed._id }));
      },
    }),

    // Fetches documents for a given feed
    getFeedPosts: build.query<SocialFeedPost[], FeedPostsPayload>({
      query: (props) => ({
        url: `/socialFeed/feed/${props.feedId}/posts`,
        method: 'GET',
        params: {
          limit: props.limit,
          offset: props.offset,
        },
      }),
      transformResponse: (response: { data: SocialFeedPost[] }) => response.data,
      providesTags: (result, error, arg) => {
        if (!result) {
          return [];
        }

        return [
          { type: 'FeedPosts', id: arg.feedId },
          ...result.map(
            (post) =>
              ({ type: 'SocialFeedPost', id: post._id } as FullTagDescription<'SocialFeedPost'>)
          ),
        ];
      },
    }),

    // Fetches a single post by id, used to open a post's comments directly from a URL
    // (e.g. a shared link, or a future notification link) without requiring the post to
    // already be present in a feed's list-query cache.
    getSocialFeedPost: build.query<SocialFeedPost, { postId: string }>({
      query: (props) => ({
        url: `/socialFeed/post/${props.postId}`,
        method: 'GET',
      }),
      transformResponse: (response: { data: SocialFeedPost }) => response.data,
      providesTags: (result) => {
        if (!result) {
          return [];
        }
        return [{ type: 'SocialFeedPost', id: result._id }];
      },
    }),

    // Gets the latest posts for a given list of feeds
    getSocialFeedPosts: build.query<SocialFeedPost[], SocialFeedPostsRequest>({
      query: (props) => ({
        url: '/socialFeed/feed/posts',
        method: 'GET',
        params: props,
      }),
      transformResponse: (response: { data: SocialFeedPost[] }) => response.data,
      providesTags: (result, error, arg) => {
        if (!result) {
          return [];
        }

        return [
          ...arg.feeds.map(
            (feed) => ({ type: 'FeedPosts', id: feed } as FullTagDescription<'FeedPosts'>)
          ),
          ...result.map(
            (post) =>
              ({ type: 'SocialFeedPost', id: post._id } as FullTagDescription<'SocialFeedPost'>)
          ),
        ];
      },
    }),

    // Gets the interaction document for the specified target
    getInteractions: build.query<Interactions, GetInteractionsRequest>({
      query: (payload) => ({
        url: `/socialFeed/target/${payload.target}/interactions`,
        method: 'GET',
      }),
      transformResponse: (response: { data: Interactions }) => {
        return response.data ?? {};
      },
      providesTags: (result, error, arg) => {
        return [{ type: 'Interactions', id: arg.target }];
      },
    }),

    // Adds a new post to the specified feed
    addPost: build.mutation<AddPostResponse, AddPostRequest>({
      query: (payload) => {
        return {
          url: '/socialFeed/post',
          method: 'POST',
          body: payload,
        };
      },
      transformResponse: (response: { data: AddPostResponse }) => {
        return response.data;
      },
      invalidatesTags: (result, error, arg) => {
        if (!result) {
          return [];
        }

        // Invalidate the cache for this social feed to trigger a refetch
        return [{ type: 'FeedPosts', id: arg.socialFeedId }];
      },
    }),

    // Edits an existing post
    editPost: build.mutation<SocialFeedPost, EditPostPayload>({
      query: (payload) => {
        return {
          url: `/socialFeed/post/${payload.postId}`,
          method: 'PATCH',
          body: {
            body: payload.body,
            existingAttachments: payload.existingAttachments,
            attachments: payload.attachments,
          },
        };
      },
      transformResponse: (response: { data: SocialFeedPost }) => {
        return response.data;
      },
      invalidatesTags: (result, error, arg) => {
        if (!result || error) {
          return [];
        }

        // Invalidates the cache for this social feed post, causing a refetch
        return [{ type: 'SocialFeedPost', id: result._id }];
      },
    }),

    deletePost: build.mutation<{ feedId: string }, DeletePostPayload>({
      query: (payload) => ({
        url: `/socialFeed/post/${payload.postId}`,
        method: 'DELETE',
      }),
      transformResponse: (response: { data: { feedId: string } }) => response.data,
      transformErrorResponse: (response: { status: string | number }) => response.status,
      invalidatesTags: (result, error, arg) => {
        if (!result || error) {
          return [];
        }

        // Invalidate the cache for this social feed to trigger a refetch
        return [{ type: 'SocialFeedPost', id: arg.postId }];
      },
    }),

    addComment: build.mutation<Interactions, { targetId: string; comment: string }>({
      query: (payload) => ({
        url: `/socialFeed/target/${payload.targetId}/comment`,
        method: 'POST',
        body: {
          comment: payload.comment,
        },
      }),
      transformResponse: (response: { data: Interactions }) => response.data,
      transformErrorResponse: (response: { status: string | number }) => response.status,
      // Pessimistic update to the cache of the interaction
      async onQueryStarted({ targetId }, { dispatch, queryFulfilled }) {
        try {
          const { data: interactionsDoc } = await queryFulfilled;
          dispatch(
            socialFeedApi.util.updateQueryData('getInteractions', { target: targetId }, (draft) => {
              Object.assign(draft, interactionsDoc);
            })
          );
        } catch (error) {
          // console.error('addReaction.onQueryStarted', error);
        }
      },
    }),

    editComment: build.mutation<
      Interactions,
      { targetId: string; commentId: string; comment: string }
    >({
      query: (payload) => ({
        url: `/socialFeed/target/${payload.targetId}/comment/${payload.commentId}`,
        method: 'PATCH',
        body: {
          comment: payload.comment,
        },
      }),
      transformResponse: (response: { data: Interactions }) => response.data,
      transformErrorResponse: (response: { status: string | number }) => response.status,
      // Pessimistic update to the cache of the interaction
      async onQueryStarted({ targetId }, { dispatch, queryFulfilled }) {
        try {
          const { data: interactionsDoc } = await queryFulfilled;
          dispatch(
            socialFeedApi.util.updateQueryData('getInteractions', { target: targetId }, (draft) => {
              Object.assign(draft, interactionsDoc);
            })
          );
        } catch (error) {
          // console.error('editComment.onQueryStarted', error);
        }
      },
    }),

    deleteComment: build.mutation<Interactions, { targetId: string; commentId: string }>({
      query: (payload) => ({
        url: `/socialFeed/target/${payload.targetId}/comment/${payload.commentId}`,
        method: 'DELETE',
      }),
      transformResponse: (response: { data: Interactions }) => response.data,
      transformErrorResponse: (response: { status: string | number }) => response.status,
      // Pessimistic update to the cache of the interaction
      async onQueryStarted({ targetId }, { dispatch, queryFulfilled }) {
        try {
          const { data: interactionsDoc } = await queryFulfilled;
          dispatch(
            socialFeedApi.util.updateQueryData('getInteractions', { target: targetId }, (draft) => {
              Object.assign(draft, interactionsDoc);
            })
          );
        } catch (error) {
          // console.error('editComment.onQueryStarted', error);
        }
      },
    }),

    addReaction: build.mutation<
      Interactions,
      { targetId: string; reaction: string; commentId?: string }
    >({
      query: (payload) => ({
        url: `/socialFeed/target/${payload.targetId}/reaction`,
        method: 'POST',
        body: payload,
      }),
      transformResponse: (response: { data: Interactions }) => {
        return response.data;
      },
      transformErrorResponse: (response: { status: string | number }) => {
        return response.status;
      },
      // Pessimistic update to the cache of the interaction
      async onQueryStarted({ targetId }, { dispatch, queryFulfilled }) {
        try {
          const { data: updatedPost } = await queryFulfilled;
          dispatch(
            socialFeedApi.util.updateQueryData('getInteractions', { target: targetId }, (draft) => {
              Object.assign(draft, updatedPost);
            })
          );
        } catch (error) {
          // console.error('addReaction.onQueryStarted', error);
        }
      },
    }),

    /*
    // To test delaying a query, this is an example of an implementation
    getSocialFeedsDelayed: build.query<CombinedSocialFeed[], SocialFeedsRequest>({
      queryFn: async (arg, _api, _extraOptions, baseQuery) => {
        await new Promise((resolve) => setTimeout(resolve, 2500));
        const result = await baseQuery({
          url: `/socialFeed/getSocialFeeds`,
          method: 'GET',
          params: arg,
        });
        if (result.error) {
          return result;
        }
        return { data: (result.data as any).data };
      },
      providesTags: (result, error, arg) => {
        if (!result) {
          return [];
        }
        return result.map((feed) => ({ type: 'SocialFeed', id: feed._id }));
      },
    }),
    */

    /**
     * Gets the notification subscription status for a given target
     */
    getNotificationStatus: build.query<GetNotificationStatusResponse, GetNotificationStatusRequest>(
      {
        query: (payload) => ({
          url: `/notifications/subscriptions/${payload.targetId}`,
          method: 'GET',
        }),
        transformResponse: (response: { data: GetNotificationStatusResponse }) => response.data,
        providesTags: (_result, _error, arg) => [{ type: 'NotificationStatus', id: arg.targetId }],
      }
    ),

    /**
     * Toggles the notification setting on a given target
     *
     * The target can be whatever ID that is used to identify the target
     */
    toggleNotification: build.mutation<ToggleNotificationResponse, ToggleNotificationPayload>({
      query: (payload) => ({
        // url: `/socialFeed/toggleNotification/${payload.feedId}`,
        url: `/notifications/subscriptions/${payload.targetId}`,
        method: 'PATCH',
        body: {
          notify: payload.notify,
        },
      }),
      transformResponse: (response: { data: ToggleNotificationResponse }) => response.data,
      // Refresh the settings page's subscriptions list (if open) to reflect the new state
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              { type: 'NotificationSubscriptions', id: arg.targetId },
              { type: 'NotificationStatus', id: arg.targetId },
            ],
      // Pessimistic update of every cached query that holds the target document
      async onQueryStarted(_payload, { dispatch, getState, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const { notify, targetId, targetDocument } = data;

          dispatch(
            socialFeedApi.util.updateQueryData('getNotificationStatus', { targetId }, (draft) => {
              draft.notify = notify;
            })
          );

          // Patches the target document where it appears in a list result
          const setNotifyInList = <T extends { _id: string; notify: boolean }>(draft: T[]) => {
            const document = draft.find((item) => item._id === targetDocument._id);
            if (document) {
              document.notify = notify;
            }
          };

          // Patches a single document result
          const setNotifyOnDocument = (draft: { notify: boolean }) => {
            draft.notify = notify;
          };

          // The tags the queries already provide let us look up every cached entry
          // that holds this document, along with the args it was fetched with. Without
          // this we would have to guess limit/offset, and a wrong guess is a silent no-op.
          const cachedEntries = socialFeedApi.util.selectInvalidatedBy(getState() as RootState, [
            { type: 'SocialFeedPost', id: targetDocument._id },
            { type: 'SocialFeed', id: targetDocument._id },
          ]);

          cachedEntries.forEach(({ endpointName, originalArgs }) => {
            switch (endpointName) {
              case 'getFeedPosts':
                dispatch(
                  socialFeedApi.util.updateQueryData(
                    'getFeedPosts',
                    originalArgs as FeedPostsPayload,
                    setNotifyInList
                  )
                );
                break;
              case 'getSocialFeedPosts':
                dispatch(
                  socialFeedApi.util.updateQueryData(
                    'getSocialFeedPosts',
                    originalArgs as SocialFeedPostsRequest,
                    setNotifyInList
                  )
                );
                break;
              case 'getSocialFeeds':
                dispatch(
                  socialFeedApi.util.updateQueryData(
                    'getSocialFeeds',
                    originalArgs as SocialFeedsRequest,
                    setNotifyInList
                  )
                );
                break;
              case 'getSocialFeed':
                dispatch(
                  socialFeedApi.util.updateQueryData(
                    'getSocialFeed',
                    originalArgs as SocialFeedRequest,
                    setNotifyOnDocument
                  )
                );
                break;
              case 'getCommonSocialFeed':
                dispatch(
                  socialFeedApi.util.updateQueryData(
                    'getCommonSocialFeed',
                    originalArgs as CommonSocialFeedRequest,
                    setNotifyOnDocument
                  )
                );
                break;
              case 'getSocialFeedPost':
                dispatch(
                  socialFeedApi.util.updateQueryData(
                    'getSocialFeedPost',
                    originalArgs as { postId: string },
                    setNotifyOnDocument
                  )
                );
                break;
              default:
                break;
            }
          });
        } catch (error) {
          // Leave the cache untouched on failure, the mutation error state drives the UI
        }
      },
    }),
  }),

  overrideExisting: false,
});

export const {
  // useVerifyTokenMutation,
  useAddPostMutation,
  useEditPostMutation,
  useDeletePostMutation,
  useAddReactionMutation,
  useAddCommentMutation,
  useEditCommentMutation,
  useDeleteCommentMutation,
  useGetCommonSocialFeedQuery,
  useGetSocialFeedQuery,
  useGetSocialFeedsQuery,
  // useGetSocialFeedsDelayedQuery,
  useGetSocialFeedPostsQuery,
  useGetSocialFeedPostQuery,
  useGetInteractionsQuery,
  useGetFeedPostsQuery,
  useGetNotificationStatusQuery,
  useToggleNotificationMutation,
} = socialFeedApi;
